import type { SagittaResult, UnitSystem } from './types';

const EPS = 1e-12;

/** Calculate sagitta using the numerically stable formula. */
export function calculateSagitta(radius: number, chordLength: number): number {
	const half = chordLength / 2;
	const r2 = radius * radius;
	const h2 = half * half;
	if (r2 <= h2 || radius <= 0) {
		return Number.NaN;
	}
	const denom = radius + Math.sqrt(r2 - h2);
	return (half * half) / denom;
}

/** Validate inputs and return error messages (empty array = valid). */
export function validateInputs(
	radius: number,
	chordLength: number,
	tolPlus?: number,
	tolMinus?: number
): string[] {
	const err: string[] = [];
	if (radius <= 0) err.push('Radius must be positive.');
	if (chordLength <= 0) err.push('Chord length must be positive.');
	if (chordLength >= 2 * radius - EPS) {
		err.push('Chord length cannot exceed the diameter of the circle.');
	}
	if (tolPlus != null && tolPlus < 0)
		err.push('Plus tolerance must be non-negative.');
	if (tolMinus != null && tolMinus < 0)
		err.push('Minus tolerance must be non-negative.');

	const effectivePlus = tolPlus != null ? Math.abs(tolPlus) : 0;
	const effectiveMinus =
		tolPlus != null && (tolMinus == null || tolMinus === 0)
			? effectivePlus
			: tolMinus != null
				? Math.abs(tolMinus)
				: 0;

	if (effectivePlus > 0 || effectiveMinus > 0) {
		if (radius - effectiveMinus - EPS <= chordLength / 2) {
			err.push(
				'Minus tolerance is too large -- it would make the arc geometry impossible for this chord length.'
			);
		}
	}
	return err;
}

/** Calculate full result with optional tolerance conversion. */
export function calculateSagittaWithTolerance(
	radius: number,
	chordLength: number,
	tolPlus?: number,
	tolMinus?: number
): SagittaResult {
	const nominal = calculateSagitta(radius, chordLength);

	if (tolPlus == null && tolMinus != null) {
		return {
			nominal,
			inputRadius: radius,
			inputChord: chordLength,
			hasToleranceDisplay: false,
		};
	}

	if (tolPlus == null) {
		return {
			nominal,
			inputRadius: radius,
			inputChord: chordLength,
			hasToleranceDisplay: false,
		};
	}

	const plusOnRadius = Math.abs(tolPlus);
	const minusOnRadius =
		tolMinus == null || tolMinus === 0 ? plusOnRadius : Math.abs(tolMinus);
	const isSymmetric = tolMinus == null || tolMinus === 0;

	if (plusOnRadius < EPS && minusOnRadius < EPS) {
		return {
			nominal,
			inputRadius: radius,
			inputChord: chordLength,
			inputTolPlus: 0,
			inputTolMinus: 0,
			isSymmetric: true,
			hasToleranceDisplay: false,
		};
	}

	const rUpper = radius - minusOnRadius;
	const rLower = radius + plusOnRadius;
	const sUpper = calculateSagitta(rUpper, chordLength);
	const sLower = calculateSagitta(rLower, chordLength);

	return {
		nominal,
		upperLimit: sUpper,
		lowerLimit: sLower,
		tolPlus: sUpper - nominal,
		tolMinus: nominal - sLower,
		inputRadius: radius,
		inputChord: chordLength,
		inputTolPlus: plusOnRadius,
		inputTolMinus: minusOnRadius,
		isSymmetric,
		hasToleranceDisplay: true,
	};
}

/** Convert a numeric value between unit systems. */
export function convertValue(
	value: number,
	fromUnit: UnitSystem,
	toUnit: UnitSystem
): number {
	if (fromUnit === toUnit) return value;
	return toUnit === 'mm' ? value * 25.4 : value / 25.4;
}
